import { Page } from "@commerce/components/page";
import {
	IntegrationsScreen,
	IntegrationsSkeleton,
} from "@commerce/integrations/IntegrationsScreen";
import {
	deliveriesQuery,
	endpointsQuery,
	integrationsSearchSchema,
} from "@commerce/integrations/meta";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AdminOnlyNotice } from "@saas/settings/components/SettingsLayout";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/integrations",
)({
	validateSearch: integrationsSearchSchema,
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	// Prefetch the view the URL asks for once, on entry. Filter changes after that are
	// client queries, not loader runs.
	shouldReload: false,
	loader: async ({ context, location }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		if (context.isOrganizationAdmin) {
			const search = integrationsSearchSchema.parse(location.search);
			await Promise.all([
				queryClient.ensureQueryData(endpointsQuery(organizationId)),
				queryClient.ensureInfiniteQueryData(
					deliveriesQuery(organizationId, search),
				),
				search.delivery
					? queryClient
							.ensureQueryData(
								orpc.webhooks.deliveries.get.queryOptions({
									input: { id: search.delivery },
								}),
							)
							.catch(() => null)
					: null,
			]);
		}
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: () => ({ meta: [{ title: `Integrations - ${config.appName}` }] }),
	component: IntegrationsPage,
});

function IntegrationsPage() {
	const { isOrganizationAdmin } = Route.useRouteContext();
	const { organizationId, dehydratedState } = Route.useLoaderData();

	if (!isOrganizationAdmin) {
		return (
			<Page width="narrow">
				<AdminOnlyNotice what="integrations" />
			</Page>
		);
	}
	return (
		<AsyncBoundary
			fallback={<IntegrationsSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<IntegrationsScreen organizationId={organizationId} />
		</AsyncBoundary>
	);
}
